/**
 * CONVERSATION mode (spec §3, §33).
 *
 * Person A and person B, each with their own language, each translated into the
 * other's. The single microphone is assigned to whoever is speaking — the UI is
 * explicit about that, because a single-device conversation app otherwise
 * produces a transcript with two A's on it and no way to tell who said what.
 *
 * `targetFor` mirrors the server's routing: A's speech is translated into B's
 * language and vice versa, and TTS speaks it in the *listener's* language.
 */

import type { LanguageCode } from "@shared/types/language";
import { languageShortLabel } from "@shared/languages";
import type { SpeakerId } from "@shared/constants";
import type { Transcript } from "@shared/types/transcript";
import type { TranslationSession } from "../lib/use-session";
import type { PiperGender } from "../lib/piper";
import { Badge, Button, Panel } from "./ui";
import { MicDock } from "./MicDock";
import { TranscriptList } from "./TranscriptList";
import { PiperVoiceControl, sampleLine } from "./PiperVoiceControl";
import { IconArrowRight, IconGlobe, IconSwap } from "./icons";

interface Props {
  session: TranslationSession;
  languageA: LanguageCode | "auto";
  languageB: LanguageCode;
  onLanguageAChange: (value: LanguageCode | "auto") => void;
  onLanguageBChange: (value: LanguageCode) => void;
  onSwap: () => void;
  canSpeak: (language: LanguageCode) => boolean;
  /** Bound to the global "speak translations" setting — the toggle here used
   *  to be local state that changed nothing. Now it actually drives speech. */
  speakOut: boolean;
  onSpeakOutChange: (value: boolean) => void;
  piperGender: PiperGender;
  onPiperGenderChange: (gender: PiperGender) => void;
}
export function ConversationMode(props: Props) {
  const { session } = props;
  const speakOut = props.speakOut;

  // The active speaker lives in the session, not here: the audio `onFrame`
  // closure and the socket both have to agree on who owns the microphone, and
  // a local copy would let the two drift apart mid-conversation.
  const activeSpeaker = session.activeSpeaker;
  const assignTo = (speaker: SpeakerId): void => {
    session.assignSpeaker(speaker);
  };

  // The listener is the other person; TTS speaks into *their* language.
  const listener: SpeakerId = activeSpeaker === "A" ? "B" : "A";
  const listenerLanguage = listener === "A" ? props.languageA : props.languageB;
  const speakerLanguage = activeSpeaker === "A" ? props.languageA : props.languageB;

  return (
    <div className="stage split">
      <div className="split__main">
        <Panel title="Speakers">
          <div className="panel__body stack" style={{ alignItems: "center", textAlign: "center" }}>
            <div className="stack" style={{ gap: "var(--space-3xs)", alignItems: "center" }}>
              <span className="field__label">Person A · {languageShortLabel(props.languageA === "auto" ? "en" : props.languageA)}</span>
              <Button
                variant={activeSpeaker === "A" ? "primary" : "default"}
                onClick={() => assignTo("A")}
                disabled={session.isStarting}
                aria-pressed={activeSpeaker === "A"}
              >
                Speak as A
              </Button>
            </div>

            <div className="row" style={{ gap: "var(--space-2xs)" }}>
              <IconArrowRight size={15} className="dim" style={{ transform: "rotate(90deg)" }} aria-hidden="true" />
              <Button size="sm" iconOnly onClick={props.onSwap} disabled={session.isLive} title="Swap the two languages" aria-label="Swap the two languages">
                <IconSwap size={15} />
              </Button>
              <IconArrowRight size={15} className="dim" style={{ transform: "rotate(-90deg)" }} aria-hidden="true" />
            </div>

            <div className="stack" style={{ gap: "var(--space-3xs)", alignItems: "center" }}>
              <span className="field__label">Person B · {languageShortLabel(props.languageB)}</span>
              <Button
                variant={activeSpeaker === "B" ? "primary" : "default"}
                onClick={() => assignTo("B")}
                disabled={session.isStarting}
                aria-pressed={activeSpeaker === "B"}
              >
                Speak as B
              </Button>
            </div>

            <div className="inset stack" style={{ padding: "var(--space-2xs) var(--space-s)", gap: "var(--space-3xs)", width: "100%", textAlign: "left" }}>
              <div className="row-between">
                <span className="eyebrow">Translate into</span>
                <Badge tone="good">{listenerLanguage === "auto" ? languageShortLabel(props.languageA) : languageShortLabel(listenerLanguage)}</Badge>
              </div>
              <div className="dim" style={{ fontSize: "var(--step--2)", lineHeight: 1.5 }}>
                {activeSpeaker === "A" ? "Person A" : "Person B"} speaks {speakerLanguage === "auto" ? "any language" : languageShortLabel(speakerLanguage)}; the
                app transcribes it and renders it in the other person's language.
              </div>
            </div>
          </div>
        </Panel>

        <div
          style={{
            padding: "var(--space-2xs) var(--space-s)",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius-m)",
            background: "var(--bg-panel)",
          }}
        >
          <PiperVoiceControl
            targetLanguage={listenerLanguage === "auto" ? props.languageB : listenerLanguage}
            gender={props.piperGender}
            onGenderChange={props.onPiperGenderChange}
            speakOut={speakOut}
            onSpeakOutChange={props.onSpeakOutChange}
            piper={session.piper}
            piperVoice={session.piperVoice}
            onEnsureVoice={() => void session.ensurePiperVoice()}
            onTestVoice={() =>
              session.testVoice(listenerLanguage === "auto" ? props.languageB : listenerLanguage, sampleLine(listenerLanguage === "auto" ? props.languageB : listenerLanguage))
            }
            onStopSpeech={session.cancelSpeech}
            speaking={session.speaking}
          />
        </div>

        <Panel className="grow" title="Live exchange" actions={<IconGlobe size={14} className="dim" />}>
          <div className="stream__body">
            <ConversationStream transcript={props.session.transcript} partial={props.session.partial} />
          </div>
        </Panel>

        <MicDock
          live={session.isLive}
          busy={session.isStarting}
          level={session.level}
          onToggle={() => void (session.isLive ? session.stop() : session.start())}
          toggleLabel={session.isLive ? `Stop — person ${activeSpeaker}` : `Start — person ${activeSpeaker}`}
          disabled={!session.isSupported}
        />
      </div>

      <div className="split__aside">
        <TranscriptList
          transcript={props.session.transcript}
          onClear={props.session.clearTranscript}
          onSpeak={(entry) => props.session.speak(entry.translatedText || entry.sourceText, entry.targetLanguage)}
          canSpeak={props.canSpeak(props.languageB)}
          emptyHint="Each finished turn is added here with its speaker, both languages and timings. Export as SRT for captions."
        />
      </div>
    </div>
  );
}

function ConversationStream({ transcript, partial }: { transcript: Transcript; partial: { speaker: SpeakerId; source: string; target: string } | null }) {
  const recent = transcript.entries.slice(-6);
  if (recent.length === 0 && !partial) {
    return (
      <div className="stream__empty">
        <span className="dim">Start the conversation. Finished turns appear here newest-last.</span>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: "var(--space-2xs)" }}>
      {recent.map((entry) => (
        <div key={entry.id} className={`inset entry--${entry.speaker.toLowerCase()}`} style={{ padding: "var(--space-2xs) var(--space-s)" }}>
          <div className="row" style={{ gap: "var(--space-2xs)", marginBottom: 2 }}>
            <span className="entry__who">{entry.speaker}</span>
            <span className="dim" style={{ fontSize: "var(--step--2)" }}>{languageShortLabel(entry.sourceLanguage)}</span>
            <span className="dim tabular" style={{ fontSize: "var(--step--2)", marginLeft: "auto" }}>
              {entry.timestamp}
            </span>
          </div>
          <div className="muted" style={{ fontSize: "var(--step--1)" }} dir="auto">{entry.sourceText}</div>
          <div style={{ fontWeight: 550 }} dir="auto">{entry.translatedText}</div>
        </div>
      ))}
      {partial ? (
        <div className="inset" style={{ padding: "var(--space-2xs) var(--space-s)", borderStyle: "dashed" }}>
          <div className="row" style={{ gap: "var(--space-2xs)", marginBottom: 2 }}>
            <span className="entry__who">{partial.speaker}</span>
            <span className="dim" style={{ fontSize: "var(--step--2)" }}>listening</span>
          </div>
          <div className="muted is-partial" style={{ fontSize: "var(--step--1)" }} dir="auto">{partial.source}</div>
          {partial.target ? <div className="is-partial" dir="auto">{partial.target}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
